// src/components/ProjectManagement/ClientsManagement.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import ClientProjectsView from './ClientProjectsView';

const ITEMS_PER_PAGE = 10;

const ClientsManagement = ({ currentUser, userRole }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClientForProjects, setSelectedClientForProjects] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    address: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  // Filter clients when search query changes
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredClients(clients);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = clients.filter(client => 
        client.name?.toLowerCase().includes(query) ||
        client.phone?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.location?.toLowerCase().includes(query)
      );
      setFilteredClients(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, clients]);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const clientsData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      clientsData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setClients(clientsData);
      setFilteredClients(clientsData);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    document.querySelector('.clients-grid-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddClient = async () => {
    if (!formData.name.trim()) {
      alert('Client name is required');
      return;
    }

    try {
      const clientData = {
        name: formData.name.trim(),
        phone: formData.phone || '',
        email: formData.email || '',
        location: formData.location || '',
        address: formData.address || '',
        createdAt: new Date(),
        createdBy: currentUser?.uid || 'unknown'
      };
      
      await addDoc(collection(db, 'clients'), clientData);
      alert('✅ Client added successfully!');
      setShowAddModal(false);
      setFormData({ name: '', phone: '', email: '', location: '', address: '' });
      fetchClients();
    } catch (error) {
      console.error('Error adding client:', error);
      alert('Failed to add client: ' + error.message);
    }
  };

  const handleUpdateClient = async () => {
    if (!formData.name.trim()) {
      alert('Client name is required');
      return;
    }

    try {
      const clientData = {
        name: formData.name.trim(),
        phone: formData.phone || '',
        email: formData.email || '',
        location: formData.location || '',
        address: formData.address || '',
        updatedAt: new Date(),
        updatedBy: currentUser?.uid || 'unknown'
      };
      
      await updateDoc(doc(db, 'clients', editingClient.id), clientData);
      alert('✅ Client updated successfully!');
      setEditingClient(null);
      setFormData({ name: '', phone: '', email: '', location: '', address: '' });
      fetchClients();
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client: ' + error.message);
    }
  };

  const handleDeleteClient = async (client) => {
    const confirmed = window.confirm(`Delete client "${client.name}"? This will NOT delete associated projects.`);
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'clients', client.id));
      alert('✅ Client deleted successfully!');
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Failed to delete client: ' + error.message);
    }
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      location: client.location || '',
      address: client.address || ''
    });
  };

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let endPage = Math.min(totalPages, startPage + maxVisible - 1);
      
      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };
    
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '16px 24px',
        marginTop: '24px',
        borderTop: '1px solid #eef2f6',
        background: '#fafafa',
        borderRadius: '16px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredClients.length)} of {filteredClients.length} clients
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: 'white',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.5 : 1,
              fontSize: '13px'
            }}
          >
            ← Prev
          </button>
          
          {getPageNumbers().map(pageNum => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: currentPage === pageNum ? 'none' : '1px solid #e5e7eb',
                background: currentPage === pageNum ? '#800020' : 'white',
                color: currentPage === pageNum ? 'white' : '#374151',
                cursor: 'pointer',
                fontWeight: currentPage === pageNum ? '600' : '400',
                minWidth: '36px'
              }}
            >
              {pageNum}
            </button>
          ))}
          
          <button 
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: 'white',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.5 : 1,
              fontSize: '13px'
            }}
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  // If viewing projects for a client, show the ClientProjectsView
  if (selectedClientForProjects) {
    return (
      <ClientProjectsView 
        client={selectedClientForProjects}
        onBack={() => setSelectedClientForProjects(null)}
        currentUser={currentUser}
      />
    );
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading clients...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state" style={{ textAlign: 'center', padding: '60px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h3>Error Loading Clients</h3>
        <p style={{ color: '#666' }}>{error}</p>
        <button 
          onClick={fetchClients} 
          style={{
            background: '#800020',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '16px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="clients-management-container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>👥 Client Management</h2>
          <p style={{ color: '#666', marginTop: '4px' }}>Manage all your clients and their contact information</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          style={{
            background: 'linear-gradient(135deg, #800020, #5a0016)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'transform 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          + Add New Client
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ 
        marginBottom: '24px',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, position: 'relative', minWidth: '250px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px' }}>🔍</span>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by name, phone, email, or location..."
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              borderRadius: '12px',
              border: '2px solid #e5e7eb',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#800020'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#999'
              }}
            >
              ✕
            </button>
          )}
        </div>
        <div style={{ fontSize: '13px', color: '#666', background: '#f3f4f6', padding: '8px 16px', borderRadius: '20px' }}>
          📋 {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px', background: '#f9fafb', borderRadius: '16px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ marginBottom: '8px' }}>{searchQuery ? 'No Matching Clients' : 'No Clients Yet'}</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            {searchQuery ? `No clients found matching "${searchQuery}"` : 'Click "Add New Client" to create your first client record'}
          </p>
          {searchQuery ? (
            <button 
              onClick={clearSearch}
              style={{
                background: '#800020',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Clear Search
            </button>
          ) : (
            <button 
              onClick={() => setShowAddModal(true)}
              style={{
                background: 'linear-gradient(135deg, #800020, #5a0016)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              + Add New Client
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="clients-grid-container">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {paginatedClients.map(client => (
                <div key={client.id} style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s ease, boxShadow 0.2s ease',
                  border: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #800020, #5a0016)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        fontWeight: 'bold'
                      }}>
                        {client.name?.charAt(0).toUpperCase() || 'C'}
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>{client.name}</h3>
                        {client.phone && <span style={{ fontSize: '12px', color: '#10b981' }}>📞 {client.phone}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => setSelectedClientForProjects(client)}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        title="View all projects for this client"
                      >
                         View Projects
                      </button>
                      <button 
                        onClick={() => openEditModal(client)}
                        style={{
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteClient(client)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                    {client.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px' }}>
                        <span>✉️</span>
                        <span style={{ color: '#666' }}>{client.email}</span>
                      </div>
                    )}
                    {(client.location || client.address) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px' }}>
                        <span>📍</span>
                        <span style={{ color: '#666' }}>{client.location || client.address}</span>
                      </div>
                    )}
                    {client.createdAt && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#999', marginTop: '8px' }}>
                        <span>📅 Added: {client.createdAt?.toDate?.()?.toLocaleDateString() || new Date(client.createdAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingClient) && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setEditingClient(null);
          setFormData({ name: '', phone: '', email: '', location: '', address: '' });
        }}>
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h3>{editingClient ? '✏️ Edit Client' : '➕ Add New Client'}</h3>
            
            <label>Client Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter client name"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #e5e7eb', marginBottom: '16px' }}
            />
            
            <label>Phone Number</label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #e5e7eb', marginBottom: '16px' }}
            />
            
            <label>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #e5e7eb', marginBottom: '16px' }}
            />
            
            <label>Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="Enter location (e.g., Kampala, Uganda)"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #e5e7eb', marginBottom: '16px' }}
            />
            
            <label>Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter full address"
              rows="2"
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #e5e7eb', marginBottom: '16px', fontFamily: 'inherit' }}
            />
            
            <div className="modal-actions" style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button 
                onClick={editingClient ? handleUpdateClient : handleAddClient}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #800020, #5a0016)',
                  color: 'white',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {editingClient ? '✓ Update Client' : '✓ Add Client'}
              </button>
              <button 
                onClick={() => {
                  setShowAddModal(false);
                  setEditingClient(null);
                  setFormData({ name: '', phone: '', email: '', location: '', address: '' });
                }}
                style={{
                  flex: 1,
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManagement;